import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { Unsubscribable } from '@bcf2-shared-pkg-v1/ui/shared/classes/unsubscribable';
import { isServer } from 'lit';
import { delay, fromEvent } from 'rxjs';

type LocationKind = {
  origin: string;
  pathname: string;
  search: string;
  hash: string;
};

function mapToFullPath(location: LocationKind): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

export class HistoryUrl extends Unsubscribable {
  private _history: string[] = [];
  private _skipNextPopState: boolean = false;

  constructor() {
    super();
    if (__RENDER_MODE__ === 'browser' && !isServer) {
      this._sub = fromEvent(window, 'popstate').subscribe(() => this._popState());
      this._sub = fromEvent(window, 'hashchange')
        .pipe(delay(10))
        .subscribe(() => this._checkUrl());

      this._history = this._getFromMemory();
      this._checkUrl();
    }
  }

  private _replaceUrl(): void {
    const newPath: string = mapToFullPath(window.location);
    if (this._history.length === 0) {
      this._history.push(newPath);
    } else {
      this._history[this._history.length - 1] = newPath;
    }
    this._updateMemory();
  }

  private _checkUrl(): void {
    const newPath: string = mapToFullPath(window.location);
    const lastHistoryUrl: string | undefined = this._history[this._history.length - 1];
    if (lastHistoryUrl === newPath) {
      return;
    }
    this._history.push(newPath);
    this._updateMemory();
  }

  private _popState(): void {
    if (this._skipNextPopState) {
      this._skipNextPopState = false;
      return;
    }
    const current: string = mapToFullPath(window.location);
    const idx: number = this._history.lastIndexOf(current);
    if (idx >= 0) {
      // back
      this._history = this._history.slice(0, idx + 1);
    } else {
      // forward
      this._history.push(current);
    }
    this._updateMemory();
  }

  public routerMightWentBack(n: number): void {
    if (this._skipNextPopState) {
      this._skipNextPopState = false;
      return;
    }
    this._skipNextPopState = true;

    this._history = this._history.slice(0, -n);
    this._updateMemory();
  }

  public routerMightUpdated(): void {
    this._checkUrl();
  }

  public routerMightReplaced(): void {
    setTimeout(() => {
      this._replaceUrl();
    }, 10);
  }

  public get history(): string[] {
    return this._history.slice();
  }

  private _updateMemory(): void {
    if (__RENDER_MODE__ === 'browser') {
      try {
        window.sessionStorage.setItem('historyUrls', JSON.stringify(this.history));
      } catch {}
    }
  }

  private _getFromMemory(): string[] {
    if (__RENDER_MODE__ !== 'browser') {
      return [];
    }
    try {
      const fromMemory: string | undefined = window.sessionStorage.getItem('historyUrls') ?? undefined;
      if (fromMemory) {
        return JSON.parse(fromMemory);
      }
      return [];
    } catch {
      return [];
    }
  }
}

export function provideHistoryUrl(): HistoryUrl {
  return provideSingleton(HistoryUrl, () => new HistoryUrl());
}
